import React, { useState } from 'react';
import styled from 'styled-components';
import { QRCodeSVG } from 'qrcode.react';

import ShareButton from '../../components/ShareButton';
import { fetchAffiliateMe, fetchAffiliateReport } from './api';
import { useAsyncResource } from '../team-admin/hooks';

// Partner (affiliate/reseller) dashboard — AFFILIATE_RESELLER_PLAN.md §4.
// Informational only in V1: payouts are settled offline against the
// per-month numbers shown here. Names are privacy-masked by the API.
const formatMoney = (cents, currency = 'eur') => {
  const amount = (Number(cents ?? 0) / 100).toFixed(2);
  return `${amount} ${String(currency).toUpperCase()}`;
};

const formatMonth = (ym) => {
  if (!ym) return '—';
  const [y, m] = ym.split('-');
  const date = new Date(Number(y), Number(m) - 1, 1);
  return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const AffiliateDashboard = () => {
  const me = useAsyncResource(fetchAffiliateMe, []);
  const report = useAsyncResource(fetchAffiliateReport, []);
  const [qrOpen, setQrOpen] = useState(false);

  const link = me.data?.link || '';
  const summary = report.data?.summary || me.data?.summary || {};
  const months = report.data?.months || [];
  const referrals = report.data?.referrals || [];
  const currency = summary.currency || 'eur';

  return (
    <Wrap>
      <Header>
        <Title>Partner dashboard</Title>
        <Sub>People who sign up through your link are tracked automatically.</Sub>
      </Header>

      {(me.loading || report.loading) && <Muted>Loading…</Muted>}
      {(me.error || report.error) && <ErrorBox>Failed to load your referral data. Please try again.</ErrorBox>}

      {!me.loading && !me.error && link && (
        <LinkCard>
          <LinkLabel>Your referral link</LinkLabel>
          <LinkField readOnly value={link} onFocus={(e) => e.target.select()} />
          <Actions>
            <ShareButton url={link} label="Share link" />
            <QrButton type="button" onClick={() => setQrOpen(true)}>Show QR code</QrButton>
          </Actions>
        </LinkCard>
      )}

      {!report.loading && !report.error && (
        <>
          <Cards>
            <Card>
              <CardTitle>Sign-ups</CardTitle>
              <CardMetric>{summary.signups ?? 0}</CardMetric>
              <CardSub>registered via your link</CardSub>
            </Card>
            <Card>
              <CardTitle>Paying users</CardTitle>
              <CardMetric>{summary.converted ?? 0}</CardMetric>
              <CardSub>converted to a subscription</CardSub>
            </Card>
            <Card>
              <CardTitle>This month</CardTitle>
              <CardMetric>{formatMoney(summary.accrued_this_month_cents, currency)}</CardMetric>
              <CardSub>commission accrued</CardSub>
            </Card>
            <Card>
              <CardTitle>Total earned</CardTitle>
              <CardMetric>{formatMoney(summary.accrued_total_cents, currency)}</CardMetric>
              <CardSub>all-time commission</CardSub>
            </Card>
          </Cards>

          <SectionTitle>Monthly breakdown</SectionTitle>
          {months.length === 0 ? (
            <Empty>No commission yet — it appears here after the first payment by a referred user.</Empty>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Paid invoices</th>
                  <th>Commission</th>
                </tr>
              </thead>
              <tbody>
                {months.map((m) => (
                  <tr key={m.month}>
                    <td>{formatMonth(m.month)}</td>
                    <td>{m.invoice_count}</td>
                    <td>
                      <Money>{formatMoney(m.accrued_cents, currency)}</Money>
                      {m.voided_cents > 0 && (
                        <Voided>−{formatMoney(m.voided_cents, currency)} refunded</Voided>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          <SectionTitle>Referred users</SectionTitle>
          {referrals.length === 0 ? (
            <Empty>No sign-ups yet. Share your link or QR code to get started.</Empty>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Joined</th>
                  <th>Status</th>
                  <th>Commission</th>
                </tr>
              </thead>
              <tbody>
                {referrals.map((r, i) => (
                  <tr key={i}>
                    <td>{r.name}</td>
                    <td>{formatDate(r.joined_at)}</td>
                    <td>
                      <Badge $on={r.status === 'converted'}>
                        {r.status === 'converted' ? 'Paying' : 'Registered'}
                      </Badge>
                    </td>
                    <td><Money>{formatMoney(r.commission_total_cents, currency)}</Money></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </>
      )}

      {qrOpen && (
        <Overlay onClick={() => setQrOpen(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <Close type="button" aria-label="Close" onClick={() => setQrOpen(false)}>✕</Close>
            <ModalTitle>Scan to sign up</ModalTitle>
            <QrWrap>
              {link ? <QRCodeSVG value={link} size={220} marginSize={2} level="M" /> : null}
            </QrWrap>
          </Modal>
        </Overlay>
      )}
    </Wrap>
  );
};

export default AffiliateDashboard;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Title = styled.h1`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 24px;
  margin: 0;
  color: ${(props) => props.theme.white};
`;

const Sub = styled.div`
  color: ${(props) => props.theme.blueish2};
  font-size: 14px;
`;

const Muted = styled.div`
  color: ${(props) => props.theme.blueish2};
  font-size: 14px;
  padding: 8px 0;
`;

const ErrorBox = styled.div`
  color: ${(props) => props.theme.white};
  background: #3a1414;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
`;

const LinkCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  background: ${(props) => props.theme.row};
  border: 1px solid ${(props) => props.theme.lines};
  border-radius: 12px;
`;

const LinkLabel = styled.span`
  color: ${(props) => props.theme.blueish2};
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: 'SatoshiMedium', sans-serif;
`;

const LinkField = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid ${(props) => props.theme.lines};
  background: ${(props) => props.theme.black};
  color: ${(props) => props.theme.white};
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 13px;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const QrButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid ${(props) => props.theme.borderColor};
  background: transparent;
  color: ${(props) => props.theme.white};
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 12px;
  letter-spacing: 0.3px;
  cursor: pointer;

  &:hover { border-color: ${(props) => props.theme.blueish2}; }
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
`;

const Card = styled.div`
  background: ${(props) => props.theme.row};
  border: 1px solid ${(props) => props.theme.lines};
  border-radius: 12px;
  padding: 18px;
`;

const CardTitle = styled.div`
  color: ${(props) => props.theme.blueish2};
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: 'SatoshiMedium', sans-serif;
`;

const CardMetric = styled.div`
  margin: 8px 0 4px;
  font-size: 28px;
  font-family: 'SatoshiBold', sans-serif;
  color: ${(props) => props.theme.green};
`;

const CardSub = styled.div`
  color: ${(props) => props.theme.blueish2};
  font-size: 13px;
`;

const SectionTitle = styled.h2`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${(props) => props.theme.blueish2};
  margin: 8px 0 0;
`;

const Empty = styled.p`
  font-family: 'SatoshiRegular', sans-serif;
  font-size: 13px;
  color: ${(props) => props.theme.blueish2};
  margin: 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${(props) => props.theme.row};
  border: 1px solid ${(props) => props.theme.lines};
  border-radius: 12px;
  overflow: hidden;

  th, td {
    text-align: left;
    padding: 12px 16px;
    font-size: 13px;
  }

  th {
    color: ${(props) => props.theme.blueish2};
    font-family: 'SatoshiMedium', sans-serif;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 1px solid ${(props) => props.theme.lines};
  }

  td {
    color: ${(props) => props.theme.white};
    font-family: 'SatoshiRegular', sans-serif;
    border-bottom: 1px solid ${(props) => props.theme.lines};
  }

  tr:last-child td { border-bottom: 0; }
`;

const Money = styled.span`
  font-family: 'SatoshiBold', sans-serif;
  color: ${(props) => props.theme.green};
`;

const Voided = styled.span`
  display: block;
  color: ${(props) => props.theme.blueish2};
  font-size: 11px;
`;

const Badge = styled.span`
  padding: 3px 10px;
  border-radius: 999px;
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 11px;
  color: ${(props) => (props.$on ? props.theme.black : props.theme.blueish2)};
  background: ${(props) => (props.$on ? props.theme.green : 'transparent')};
  border: 1px solid ${(props) => (props.$on ? props.theme.green : props.theme.lines)};
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.7);
  padding: 24px;
`;

const Modal = styled.div`
  position: relative;
  background: ${(props) => props.theme.row};
  border: 1px solid ${(props) => props.theme.lines};
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  max-width: 320px;
  width: 100%;
`;

const ModalTitle = styled.h3`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 16px;
  margin: 0;
  color: ${(props) => props.theme.white};
`;

const QrWrap = styled.div`
  background: #ffffff;
  padding: 16px;
  border-radius: 10px;
  line-height: 0;
`;

const Close = styled.button`
  position: absolute;
  top: 10px;
  right: 12px;
  background: transparent;
  border: 0;
  color: ${(props) => props.theme.blueish2};
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
`;
